import { motion, useReducedMotion } from 'framer-motion'
import { Dumbbell } from 'lucide-react'
import { ProductPreview } from './ProductPreview'

export function Hero() {
  const reduceMotion = useReducedMotion()

  const itemTransition = {
    duration: reduceMotion ? 0.6 : 0.7,
    ease: 'easeOut' as const,
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: reduceMotion ? 0 : 0.1,
        delayChildren: 0,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: itemTransition,
    },
  }

  return (
    <section className="relative isolate flex min-h-[105svh] flex-col overflow-hidden bg-charcoal-black">
      <div className="bs-ambient" aria-hidden />
      <div className="absolute left-4 top-5 z-10 flex items-center gap-2 rounded-xl border border-white/[0.08] bg-charcoal-black/55 px-3 py-2 backdrop-blur md:left-7 md:top-7">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-electric-cyan/30 bg-electric-cyan/10 text-electric-cyan">
          <Dumbbell className="h-4 w-4" aria-hidden />
        </span>
        <span className="text-[15px] font-semibold tracking-tight text-soft-white">Boomshift</span>
      </div>

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pb-14 pt-24 md:pb-16 md:pt-28">
        <motion.div
          className="mx-auto max-w-4xl text-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <p className="inline-flex items-center gap-2 rounded-full border border-electric-cyan/25 bg-electric-cyan/[0.06] px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-electric-cyan/90">
              <span
                className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-electric-cyan"
                aria-hidden
              />
              Private beta for invited clients
            </p>
            <h1 className="mt-6 text-[clamp(2.65rem,8vw,6.25rem)] font-semibold leading-[0.99] tracking-[-0.02em] text-soft-white">
              <span className="block">Your trainer. Your plan.</span>
              <span className="mt-1.5 block bg-gradient-to-r from-soft-white via-soft-white to-electric-cyan bg-clip-text text-transparent">
                In one place.
              </span>
            </h1>
          </motion.div>

          <motion.p
            className="mx-auto mt-7 max-w-[53ch] text-[1.1rem] leading-relaxed text-muted-silver md:text-[1.45rem] md:leading-[1.45]"
            variants={itemVariants}
          >
            Boomshift is the app real trainers use to coach real people who don&apos;t have time to be
            pro athletes. Workouts, food, check-ins, and progress — together in one place. So you can
            stop juggling spreadsheets and start showing up.
          </motion.p>

          <motion.p
            className="mx-auto mt-5 max-w-xl text-sm tracking-[0.01em] text-muted-silver"
            variants={itemVariants}
          >
            Built for trainers coaching real clients between check-ins.
          </motion.p>

          <motion.div variants={itemVariants}>
            <div className="mt-9 flex flex-col items-center justify-center gap-3.5">
              <div className="relative inline-flex">
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  title="Self-serve signup isn't open yet — sign in if your trainer invited you."
                  className="relative h-12 cursor-not-allowed rounded-xl bg-gradient-to-b from-charcoal to-slate-gray px-8 text-base font-medium text-soft-white/60 shadow-sm"
                >
                  Start free trial
                </button>
                <span
                  aria-hidden
                  className="pointer-events-none absolute -top-2.5 -right-2.5 rounded-full border border-warm-amber/40 bg-charcoal-black/95 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-warm-amber shadow-md"
                >
                  Coming soon
                </span>
              </div>
            </div>
            <p className="mt-6 text-sm text-muted-silver">Private access is currently trainer-managed.</p>
          </motion.div>
        </motion.div>

        <motion.div
          className="mt-10 w-full md:mt-12"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut', delay: 0.3 }}
        >
          <ProductPreview />
        </motion.div>
      </div>
    </section>
  )
}
